<template>
  <div class="flex flex-col w-full h-full justify-items-end">
    <BaseDetailsPreviewEditHeader
      v-if="isModified"
      hideLayoutOptions
      title=""
      mode="edit"
      @cancel="resetSelection"
      @save="onSaveActionWithDetail" />
    <div class="flex flex-1 pt-2">
      <div class="w-[40%] h-full" @focusout="isDropdownOpen = false">
        <div class="px-2">{{ $t('pages.qst-actions') }}</div>
        <BaseTable
          v-if="actions"
          :headers="actionHeaders"
          :list="filteredData"
          table-id="actions"
          :clickable="true"
          :selected-item="selectedAction ? selectedAction : []"
          @rightClicked="onDropdownClicked"
          @add-new="onAddNewAction"
          @tableItemClicked="handleSelectAction"
          @refresh-data="refreshData"
          hide-grouping
          is-saving-layout-hidden
          is-download-button-hidden>
        </BaseTable>
      </div>
      <div class="w-[60%] border-gray-300 border-l-2">
        <div class="px-2">{{ $t('qst-actions.actionDetail') }}</div>
        <section>
          <ActionDetailTable
            v-if="selectedAction"
            v-model="selectedAction"
            @modified="
              actionsToAddEdit.includes(selectedAction.id)
                ? ''
                : actionsToAddEdit.push(selectedAction.id)
            "></ActionDetailTable>
        </section>
      </div>
    </div>
  </div>
  <ActionModalCreateEdit
    v-if="createActionActive"
    :qst-action="action"
    :title="actionModalMode == 'add' ? $t('qst-actions.addAction') : $t('qst-actions.editAction')"
    :mode="actionModalMode"
    @close-modal="createActionActive = false"
    @save="onSaveAction" />
  <ContextMenu
    :options="contextMenuOptions"
    @close="isDropdownOpen = false"
    class="fixed"
    :isDropdownOpen="isDropdownOpen"
    :style="{ top: clickLocation.y + 'px', left: clickLocation.x + 'px' }"
    @action="onAction" />

  <BaseModalConfirm
    v-if="modalDeleteRow"
    @cancel="modalDeleteRow = false"
    @confirm="onDeleteSingle()" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';
import { getQSTActions, addOrEditActions, removeActionsByIds } from '@/services/api/questionnaires';
import ContextMenu from '@/components/common/menus/ContextMenu.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import ActionModalCreateEdit from '@/components/questionnaires/ActionModalCreateEdit.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';

import { type QSTActionDetail } from '@/types/QSTActionDetails';
import { type QSTAction } from '@/types/QSTActions';
import type { TableFilterData } from '@/types/Base';
import { filterData, sortData } from '@/utils/table';
import { useI18n } from 'vue-i18n';
import ActionDetailTable from './ActionDetailTable.vue';
import { deepCopy } from '@/utils/data';

const selectedAction = ref<QSTAction | null>(null);
const action = ref<QSTAction>(initializeQSTAction());
const isDropdownOpen = ref(false);
const clickLocation = ref({ x: 0, y: 0 });
const actionsToDelete = ref<string[]>([]);
const actionsToAddEdit = ref<string[]>([]);
const actions = ref<QSTAction[]>([]);
const originalActions = ref<QSTAction[]>([]);
const createActionActive = ref(false);
const actionModalMode = ref<'add' | 'edit'>('add');
const toastStore = useToastStore();
const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  { value: 'sequence', text: $t('material.sequence'), type: 'text', active: true },
  { value: 'isSettled', text: $t('qst-actions.settled'), type: 'check', active: true },
  { value: 'description', text: $t('qst-actions.description'), type: 'text', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const actionHeaders = [
  { value: 'description', text: $t('qst-actions.description'), type: 'text', active: true },
];

function initializeQSTAction(): QSTAction {
  return {
    id: uuidv4(),
    description: '',
    isFromManager: true,
    isActive: false,
    qstActionDetails: [],
  };
}

function onDropdownClicked(data: any) {
  isDropdownOpen.value = true;
  clickLocation.value = {
    x: data.event.clientX,
    y: data.event.clientY,
  };
  handleSelectAction(data.item);
}

function resetSelection() {
  actions.value = deepCopy<QSTAction[]>(originalActions.value);
  if (selectedAction.value != null) {
    const newActionReference = actions.value.find((i) => i.id == selectedAction.value?.id);
    if (newActionReference) {
      selectedAction.value = newActionReference;
    }
  }
}

async function loadData() {
  const res = await getQSTActions();
  if (res) {
    originalActions.value = deepCopy<QSTAction[]>(res);
    resetSelection();
  }
}

const isModified = computed(() => {
  return JSON.stringify(originalActions.value) != JSON.stringify(actions.value);
});

onMounted(async () => {
  await loadData();
});

function handleSelectAction(item: QSTAction | null) {
  selectedAction.value = item;
  if (item && item.qstActionDetails && selectedAction.value) {
    selectedAction.value.qstActionDetails = item.qstActionDetails;
  }
}

async function onDeleteSingle() {
  if (selectedAction.value) {
    const index = originalActions.value.findIndex((item) => {
      return item.id == selectedAction.value?.id;
    });
    if (index >= 0) {
      actionsToDelete.value.push(selectedAction.value.id);
    } else {
      actionsToAddEdit.value = actionsToAddEdit.value.filter(
        (item) => item != selectedAction.value?.id
      );
    }
    actions.value = actions.value.filter((item) => {
      if (selectedAction.value) {
        return item.id != selectedAction.value.id;
      }
    });
    handleSelectAction(null);
    modalDeleteRow.value = false;
  }
}

function onAddNewAction() {
  action.value = initializeQSTAction();
  createActionActive.value = true;
  actionModalMode.value = 'add';
}

function onEditAction(item: QSTAction) {
  action.value = item;
  actionModalMode.value = 'edit';
  createActionActive.value = true;
}

function onSaveAction(modifiedData: QSTAction) {
  const index = actions.value.findIndex((item) => item.id === modifiedData.id);
  actionsToAddEdit.value.push(modifiedData.id);
  if (index !== -1) actions.value[index] = modifiedData;
  else actions.value.push(modifiedData);
  createActionActive.value = false;
}

function onDuplicate(item: QSTAction) {
  const newVal: QSTAction = { ...item, id: uuidv4() };
  newVal.qstActionDetails = newVal.qstActionDetails.map((item: QSTActionDetail) => {
    return { ...item, id: uuidv4() };
  });
  actions.value.push(newVal);
  actionsToAddEdit.value.push(newVal.id);
  handleSelectAction(newVal);
}
const filterQuery = ref<TableFilterData>();

async function refreshData(data: TableFilterData) {
  filterQuery.value = data;
}
const filteredData = computed(() => {
  let finalData = Object.assign(actions.value);
  if (filterQuery.value?.query) {
    finalData = filterData(finalData, filterQuery.value?.query, ['description']);
  }

  if (filterQuery.value?.column !== undefined && filterQuery.value?.order) {
    finalData = sortData(
      finalData,
      filterQuery.value.column,
      filterQuery.value?.order,
      tableHeaders
    );
  }
  return finalData;
});

const contextMenuOptions = [
  { name: 'tree.rename', action: 'rename', icon: 'fluent:rename-24-regular' },
  { name: 'tree.duplicate', action: 'duplicate', icon: 'fad:duplicate' },
  { name: 'tree.deleteItem', action: 'delete', icon: 'tdesign:delete-1', color: 'text-red-500' },
];

function onAction(action: string) {
  if (!selectedAction.value) return;
  if (action == 'rename') {
    onEditAction(selectedAction.value);
  }
  if (action == 'delete') onDeleteRow(selectedAction.value);
  if (action == 'duplicate') onDuplicate(selectedAction.value);

  isDropdownOpen.value = false;
}
const modalDeleteRow = ref(false);
const deleteRowItem = ref<QSTAction>();
function onDeleteRow(item: QSTAction) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onSaveActionWithDetail() {
  if (!actions.value) return;
  if (actionsToDelete.value.length) {
    const deleteRes = await removeActionsByIds(actionsToDelete.value);
    if (deleteRes) toastStore.error({ text: $t('toast.error') });
    else toastStore.success({ text: $t('baseObject.deleted') });
    if (!actionsToAddEdit.value.length) loadData();
  }
  const isError = ref(false);
  actions.value = actions.value.filter((action) => {
    return actionsToAddEdit.value.includes(action.id);
  });
  for (let i = 0; i < actions.value?.length; i++) {
    const res = await addOrEditActions(actions.value[i]);
    if (!res) {
      isError.value = true;
    }
  }
  if (isError.value) {
    toastStore.error({ text: $t('toast.error') });
  } else {
    loadData();
    toastStore.success({ text: $t('qst-actions.qstActionUpdated') });
  }
}
</script>
